import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type { Course, CourseContent, CourseCreate, CourseListResponse, CourseReorderItem, CourseUpdate } from '../types'

export function useCourses(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ['courses', page, pageSize],
    queryFn: async () => {
      const { data } = await api.get<CourseListResponse>(
        `/api/v1/courses/?page=${page}&page_size=${pageSize}`,
      )
      return data
    },
  })
}

export function useCourse(id: string) {
  return useQuery({
    queryKey: ['courses', id],
    queryFn: async () => {
      const { data } = await api.get<CourseContent>(`/api/v1/courses/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreateCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CourseCreate) => {
      const { data } = await api.post<Course>('/api/v1/courses/', body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  })
}

export function useUpdateCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: CourseUpdate & { id: string }) => {
      const { data } = await api.patch<Course>(`/api/v1/courses/${id}`, body)
      return data
    },
    onSuccess: (course) => {
      qc.invalidateQueries({ queryKey: ['courses'] })
      qc.invalidateQueries({ queryKey: ['courses', course.id] })
    },
  })
}

export function useDeleteCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/courses/${id}`)
      return id
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  })
}

export function useAddDeckToCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ courseId, deckId }: { courseId: string; deckId: string }) => {
      await api.post(`/api/v1/courses/${courseId}/decks`, { deck_id: deckId })
    },
    onSuccess: (_, { courseId }) => qc.invalidateQueries({ queryKey: ['courses', courseId] }),
  })
}

export function useRemoveDeckFromCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ courseId, deckId }: { courseId: string; deckId: string }) => {
      await api.delete(`/api/v1/courses/${courseId}/decks/${deckId}`)
    },
    onSuccess: (_, { courseId }) => qc.invalidateQueries({ queryKey: ['courses', courseId] }),
  })
}

export function useAddFolderToCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ courseId, folderId }: { courseId: string; folderId: string }) => {
      await api.post(`/api/v1/courses/${courseId}/folders`, { folder_id: folderId })
    },
    onSuccess: (_, { courseId }) => qc.invalidateQueries({ queryKey: ['courses', courseId] }),
  })
}

export function useReorderCourseItems() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ courseId, items }: { courseId: string; items: CourseReorderItem[] }) => {
      await api.patch(`/api/v1/courses/${courseId}/reorder`, { items })
      return { courseId }
    },
    onSuccess: ({ courseId }) => qc.invalidateQueries({ queryKey: ['courses', courseId] }),
  })
}

export function useRemoveFolderFromCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ courseId, folderId }: { courseId: string; folderId: string }) => {
      await api.delete(`/api/v1/courses/${courseId}/folders/${folderId}`)
    },
    onSuccess: (_, { courseId }) => qc.invalidateQueries({ queryKey: ['courses', courseId] }),
  })
}
